/*
  Homepage behaviour, ported from the Webflow IX2 config of the live site:
  - Preloader: the client's logo-flip animation (mf-intro-fast.webp in Files, 1.3s; skipped when the URL has a #hash, e.g. the header logo's /#top) once decoded, then the two words
    fly apart (±110vw, 500ms easeInOut) and the hero scales back in (500ms easeInOut).
  - Hero arrow icons act as the cursor over the slider halves (≥768px).
  - Slider counter "1/8".
*/
(function () {
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const INTRO_MS = 1300; // length of the client's logo-flip animation (mf-intro.webp, plays once)
  const INTRO_TIMEOUT_MS = 1500; // slow connection: skip the flip rather than hold the page

  async function playIntro(preloader) {
    const intro = preloader.querySelector('[data-mf-intro]');
    if (!intro || !intro.dataset.src) return;
    intro.src = intro.dataset.src;
    const decoded = intro.decode ? intro.decode().then(() => true, () => false) : Promise.resolve(true);
    if (!(await Promise.race([decoded, wait(INTRO_TIMEOUT_MS).then(() => false)]))) return;
    // Shown only once decoded, so the animation starts from its first frame
    intro.classList.add('is-playing');
    await wait(INTRO_MS);
  }

  async function runPreloader(preloader) {
    const hero = document.querySelector('[data-mf-hero]');

    await playIntro(preloader);

    // "move-preloader": words shown, split after 1s, hero scales in
    preloader.classList.add('is-words');
    await wait(450);
    preloader.classList.add('is-split');
    await wait(500);
    preloader.classList.add('is-done');
    if (hero) {
      hero.classList.add('is-revealing');
      requestAnimationFrame(() => hero.classList.remove('is-preloading'));
    }
  }

  function initFollowIcons(slider) {
    // The arrow halves hide the real cursor, so the icon acts as the cursor: it sits on the pointer
    // with light smoothing. (The live Webflow mapping left the icon ~400px above the pointer and it
    // could never reach the bottom of the screen — fixed intentionally.)
    const desktop = window.matchMedia('(min-width: 768px)');
    const arrows = Array.from(slider.querySelectorAll('.is-test'))
      .map((box) => ({ box, icon: box.querySelector('[data-mf-follow]'), x: 0, y: 0, tx: 0, ty: 0 }))
      .filter((arrow) => arrow.icon);
    if (!arrows.length) return;

    let frame = null;
    const half = (arrow) => arrow.icon.offsetWidth / 2;
    const render = (arrow) => {
      arrow.icon.style.transform = `translate(${arrow.x}px, ${arrow.y}px)`;
    };

    const tick = () => {
      let moving = false;
      arrows.forEach((arrow) => {
        arrow.x += (arrow.tx - arrow.x) * 0.35;
        arrow.y += (arrow.ty - arrow.y) * 0.35;
        if (Math.abs(arrow.tx - arrow.x) > 0.5 || Math.abs(arrow.ty - arrow.y) > 0.5) moving = true;
        render(arrow);
      });
      frame = moving ? requestAnimationFrame(tick) : null;
    };

    const center = (arrow) => {
      arrow.tx = arrow.x = arrow.box.clientWidth / 2 - half(arrow);
      arrow.ty = arrow.y = arrow.box.clientHeight / 2 - half(arrow);
      render(arrow);
    };

    arrows.forEach((arrow) => {
      const toPointer = (event) => {
        const rect = arrow.box.getBoundingClientRect();
        arrow.tx = event.clientX - rect.left - half(arrow);
        arrow.ty = event.clientY - rect.top - half(arrow);
      };
      arrow.box.addEventListener('mouseenter', (event) => {
        if (!desktop.matches) return;
        toPointer(event);
        arrow.x = arrow.tx;
        arrow.y = arrow.ty;
        render(arrow);
      });
      arrow.box.addEventListener('mousemove', (event) => {
        if (!desktop.matches) return;
        toPointer(event);
        if (!frame) frame = requestAnimationFrame(tick);
      });
      center(arrow);
    });

    window.addEventListener('resize', () => arrows.forEach(center));
  }

  function initCounter(slider) {
    const counter = slider.querySelector('.slider-count');
    if (!counter) return;
    slider.addEventListener('mf-slider:change', (event) => {
      counter.textContent = `${event.detail.index + 1}/${event.detail.total}`;
    });
  }

  function init() {
    const preloader = document.querySelector('[data-mf-preloader]');
    if (preloader) {
      if ((window.Shopify && window.Shopify.designMode) || location.hash) {
        preloader.classList.add('is-done');
        document.querySelector('[data-mf-hero]')?.classList.remove('is-preloading');
      } else {
        runPreloader(preloader);
      }
    }

    document.querySelectorAll('[data-mf-hero-slider]').forEach((slider) => {
      initFollowIcons(slider);
      initCounter(slider);
    });

    document.querySelectorAll('[data-mf-capabilities]').forEach(initCapabilities);
  }

  /*
    Capabilities (desktop), ported from the live site:
    - Webflow "links" scroll spy: a link is current while its section covers the viewport midpoint.
    - Anchor links scroll smoothly to their section.
    - IX2 "Image Hover Mouse Movement": the "See project" image is centred on the pointer
      (translate -50→50vw / -50→50vh, resting at 0, smoothing 50).
    - IX2 "show-item"/"hide-item" on the grid: fade in 200ms after entering (300ms), fade out 200ms.
    (Webflow also has an "Image Mouse Hover" action on the capability names, but it has no visible effect
      on the live page, so it is intentionally not ported.)
  */
  function initCapabilities(grid) {
    const sections = Array.from(grid.querySelectorAll('.bg-section'));
    const links = Array.from(grid.querySelectorAll('[data-mf-capability-link]'));
    const masks = Array.from(grid.querySelectorAll('[data-mf-capability-mask]'));
    if (!sections.length) return;

    // Scroll spy
    let ticking = false;
    const spy = () => {
      ticking = false;
      const top = window.scrollY;
      const viewport = window.innerHeight;
      const half = viewport * 0.5;
      sections.forEach((section) => {
        const sectionTop = section.getBoundingClientRect().top + top;
        const active =
          section.offsetParent !== null &&
          sectionTop + section.offsetHeight - half >= top &&
          sectionTop + half <= top + viewport;
        links.forEach((link) => {
          if (link.getAttribute('href') === `#${section.id}`) link.classList.toggle('w--current', active);
        });
      });
    };
    const requestSpy = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(spy);
    };
    window.addEventListener('scroll', requestSpy, { passive: true });
    window.addEventListener('resize', requestSpy);
    spy();

    links.forEach((link) =>
      link.addEventListener('click', (event) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
    );

    // Image follows the pointer
    const position = { x: 0, y: 0 };
    const pointer = { x: 0, y: 0 };
    let frame = null;
    const place = () =>
      masks.forEach((mask) => {
        mask.style.translate = `${-50 + 100 * position.x}vw ${-50 + 100 * position.y}vh`;
      });
    const step = () => {
      position.x += (pointer.x - position.x) * 0.2;
      position.y += (pointer.y - position.y) * 0.2;
      place();
      const moving = Math.abs(pointer.x - position.x) > 0.0001 || Math.abs(pointer.y - position.y) > 0.0001;
      if (!moving) {
        position.x = pointer.x;
        position.y = pointer.y;
        place();
      }
      frame = moving ? requestAnimationFrame(step) : null;
    };
    window.addEventListener('mousemove', (event) => {
      pointer.x = event.clientX / window.innerWidth;
      pointer.y = event.clientY / window.innerHeight;
      if (!frame) frame = requestAnimationFrame(step);
    });
    place();

    // Opacity fades (a new fade interrupts the running one, like IX2)
    const freeze = (property) =>
      masks.forEach((mask) => {
        const value = getComputedStyle(mask)[property];
        mask.getAnimations().forEach((animation) => {
          const keyframes = animation.effect && animation.effect.getKeyframes ? animation.effect.getKeyframes() : [];
          if (keyframes.some((keyframe) => property in keyframe)) animation.cancel();
        });
        mask.style[property] = value;
      });
    const animateAll = (property, to, duration, { delay = 0, easing = 'ease' } = {}) =>
      Promise.all(
        masks.map((mask) => {
          const from = getComputedStyle(mask)[property];
          const animation = mask.animate([{ [property]: from }, { [property]: String(to) }], { duration, delay, easing, fill: 'forwards' });
          return animation.finished
            .then(() => {
              mask.style[property] = String(to);
              animation.cancel();
            })
            .catch(() => {});
        })
      );

    // Timing measured on the live site: fade in 200ms after entering over 300ms, fade out over 200ms (linear).
    grid.addEventListener('mouseenter', () => {
      freeze('opacity');
      animateAll('opacity', 1, 300, { delay: 200, easing: 'linear' });
    });
    grid.addEventListener('mouseleave', () => {
      freeze('opacity');
      animateAll('opacity', 0, 200, { easing: 'linear' });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
